import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { useIsMobile, MobilePage, Icon } from '../../mobile';

/**
 * Sales Requests — Overview page.
 *
 * Cards are organized into 4 functional groups for fast navigation:
 *   - New Accounts          (account-onboarding flows)
 *   - Property Maintenance  (compliance / property-side tools)
 *   - Towing                (refunds, no-charge, discount, take-back, bad-tow)
 *   - Events                (vendor / sponsorship events)
 *
 * Each group renders:
 *   - Active buckets pulled from /requests/types (real count card)
 *   - Linked cards — clickable shortcuts to existing modules (e.g. Notices)
 *   - Coming Soon placeholders — visual-only, not clickable
 *
 * Scope is enforced server-side (/requests/stats): sales rep sees own,
 * supervisor sees their reports' work, admin sees everything. The UI
 * just renders whatever the server returned.
 */

const SCOPE_LABELS = {
  own: "Your submissions this month",
  reports: "You + your reports this month",
  all: "Company-wide this month",
};

// Position-based theme. The 1st card in every section gets POSITION_COLORS[0],
// the 2nd gets POSITION_COLORS[1], etc. This overrides the per-bucket color
// on the overview page so every section reads the same way visually.
// 7th+ slot falls back to slate (no section has more than 6 today).
const POSITION_COLORS = [
  '#3b82f6', // 1 — Blue
  '#f59e0b', // 2 — Amber
  '#10b981', // 3 — Emerald
  '#8b5cf6', // 4 — Violet
  '#ec4899', // 5 — Pink
  '#475569', // 6 — Slate
];
const colorForPosition = (i) => POSITION_COLORS[i] || POSITION_COLORS[POSITION_COLORS.length - 1];

// Bucket groups. Each group lists which active bucket keys belong to it,
// optional linked cards (clickable shortcuts to other modules), and
// coming-soon placeholders. Any active bucket key not listed in a group
// falls through to the "Other" catch-all at the bottom so we never hide
// a real bucket by accident.
const BUCKET_GROUPS = [
  {
    // Towing moved to top May 23 — highest-volume buckets get prime real estate.
    key: 'towing',
    label: 'Towing',
    activeKeys: ['refund', 'no_charge', 'discount', 'take_back', 'bad_tow'],
    linkedCards: [],
    placeholders: [],
    // Explicit display order — Phase 2.3. Bad Tow stays last (GM-only).
    order: ['discount', 'no_charge', 'take_back', 'refund', 'bad_tow'],
  },
  {
    key: 'property_maintenance',
    label: 'Property Maintenance',
    activeKeys: ['sign_request', 'tagging_request', 'gate_access', 'audit'],
    linkedCards: [],
    placeholders: [
      { key: 'hot_list',     label: 'Hot List',     icon: '🔥', color: '#dc2626' },
      { key: 'do_not_tow',   label: 'Do Not Tow',   icon: '🚷', color: '#7c3aed' },
    ],
    // Explicit display order — Phase 2.3.
    order: ['sign_request', 'tagging_request', 'gate_access', 'do_not_tow', 'hot_list', 'audit'],
  },
  {
    key: 'new_accounts',
    label: 'New Accounts',
    activeKeys: [],
    // These four cards absorb the items that used to live on the
    // /sales/accounts hub page (May 20 Phase 2). The underlying routes
    // and pages are unchanged — only the entry point moved.
    linkedCards: [
      {
        key: 'new_account_submission',
        label: 'New Account Submission',
        icon: '🏢',
        color: '#0ea5e9',
        description: 'Submit a new account with service agreement + property info docs.',
        to: '/sales/accounts/new',
      },
      {
        key: 'e_service_agreement',
        label: 'Electronic Service Agreement',
        icon: '📝',
        color: '#10b981',
        description: 'Fill out and sign a digital service agreement.',
        to: '/sales/service-agreement',
      },
      {
        key: 'e_property_info_sheet',
        label: 'Electronic Property Info Sheet',
        icon: '📋',
        color: '#8b5cf6',
        description: 'Digital property information sheet.',
        to: '/sales/property-info',
      },
      {
        key: 'notices',
        label: 'Notices',
        icon: '📄',
        color: '#f59e0b',
        description: 'Generate property notices (DOCX + PDF).',
        to: '/sales/notices',
      },
    ],
    placeholders: [],
  },
  {
    // Group renamed from 'Events' → 'Requests' May 20 Phase 2.2 when
    // Submit Expense was absorbed here from the sidebar.
    key: 'events',
    label: 'Requests',
    activeKeys: ['item_request', 'striping_bid', 'valet_trash', 'sponsored_event'],
    linkedCards: [
      {
        key: 'submit_expense',
        label: 'Expenses',
        icon: '💵',
        color: '#16a34a',
        description: 'Submit a reimbursement or company expense.',
        // Lands on the history page (KPIs + filters + Submit new button) like
        // every other bucket — not jumped straight into the form.
        to: '/sales/expenses',
      },
    ],
    placeholders: [
      // Striping Bid, Valet/Bulk Trash, and Sponsored Event are all live now
      // (see activeKeys). Bounce/Cash remain Coming Soon until specs land.
      { key: 'bounce_house',     label: 'Bounce House',                  icon: '🎪', color: '#f59e0b' },
      { key: 'cash_machine',     label: 'Cash Machine',                  icon: '💰', color: '#16a34a' },
    ],
    // Explicit display order. Item Request sits right after Expenses per
    // Matt's spec (Jul 22); Striping Bid follows.
    order: ['submit_expense', 'item_request', 'striping_bid', 'valet_trash', 'sponsored_event', 'bounce_house', 'cash_machine'],
  },
];

// Click destinations for active buckets that don't follow the default
// /sales/requests/:key pattern. Audit has its own wizard.
function bucketNavTarget(key) {
  if (key === 'audit') return '/sales/audits/new';
  return `/sales/requests/${key}`;
}

export default function RequestsOverviewPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [q, setQ] = useState('');
  const [types, setTypes] = useState([]);
  const [counts, setCounts] = useState({});
  const [scopeKind, setScopeKind] = useState('own');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [typeList, stats] = await Promise.all([
          api.getRequestTypes(),
          api.getRequestStats(),
        ]);
        if (!mounted) return;
        setTypes(Array.isArray(typeList) ? typeList : []);
        setCounts(stats?.monthly_counts || {});
        setScopeKind(stats?.scope_kind || 'own');
      } catch (err) {
        if (!mounted) return;
        setError(err.message || 'Failed to load');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Partition active types into groups (preserving any unmatched types
  // for the catch-all "Other" group so we never accidentally hide one).
  const typesByKey = Object.fromEntries(types.map(t => [t.key, t]));
  const assignedKeys = new Set();
  for (const g of BUCKET_GROUPS) g.activeKeys.forEach(k => assignedKeys.add(k));
  const unmatchedTypes = types.filter(t => !assignedKeys.has(t.key));

  // ── Phone: a searchable list, one row per thing you can submit ─────
  // A rep on a property wants to find "Refund" or "Sign Request" and tap
  // it. Rows, not 190px cards; coming-soon placeholders are left off.
  if (isMobile) {
    const needle = q.trim().toLowerCase();
    const groups = [
      ...BUCKET_GROUPS.map(g => ({
        label: g.label,
        rows: [
          ...g.activeKeys.map(k => typesByKey[k]).filter(Boolean).map(t => ({ kind: 'active', key: t.key, label: t.label, icon: t.icon, sub: t.description, count: counts[t.key] ?? 0, to: bucketNavTarget(t.key) })),
          ...(g.linkedCards || []).map(c => ({ kind: 'linked', key: c.key, label: c.label, icon: c.icon, sub: c.description, to: c.to })),
        ].sort((a, b) => {
          const rank = new Map((g.order || []).map((k, i) => [k, i]));
          const ra = rank.has(a.key) ? rank.get(a.key) : 1e9; const rb = rank.has(b.key) ? rank.get(b.key) : 1e9;
          return ra - rb;
        }),
      })),
      { label: 'Other', rows: unmatchedTypes.map(t => ({ kind: 'active', key: t.key, label: t.label, icon: t.icon, sub: t.description, count: counts[t.key] ?? 0, to: bucketNavTarget(t.key) })) },
    ].map(g => ({ ...g, rows: needle ? g.rows.filter(r => `${r.label} ${r.sub || ''}`.toLowerCase().includes(needle)) : g.rows }))
     .filter(g => g.rows.length > 0);
    return (
      <MobilePage title="Submit" subtitle={SCOPE_LABELS[scopeKind] || SCOPE_LABELS.own}
        search={q} onSearch={setQ} searchPlaceholder="Find a request type">
        {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
        {loading ? <p style={{ color: 'var(--text-muted)' }}>Loading…</p> : groups.length === 0 ? (
          <div className="m-card" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 28 }}>Nothing matches “{q}”.</div>
        ) : groups.map(g => (
          <div key={g.label} style={{ marginBottom: 6 }}>
            <div className="m-section">{g.label}</div>
            <div className="m-card" style={{ padding: 0, overflow: 'hidden' }}>
              {g.rows.map((r, i) => (
                <div key={r.key} role="button" tabIndex={0} onClick={() => navigate(r.to)}
                  onKeyDown={e => { if (e.key === 'Enter') navigate(r.to); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 60, padding: '10px 14px', cursor: 'pointer',
                    borderTop: i === 0 ? 0 : '1px solid var(--border-subtle)' }}>
                  <span style={{ fontSize: 24, width: 32, textAlign: 'center', flexShrink: 0 }}>{r.icon || '📋'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>{r.label}</div>
                    {r.sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.sub}</div>}
                  </div>
                  {r.kind === 'active' && r.count > 0 && (
                    <span className="m-pill" style={{ background: 'var(--bg-inset)', color: 'var(--text-secondary)' }}>{r.count} this month</span>
                  )}
                  <Icon name="chevron" size={18} color="#cbd5e1" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </MobilePage>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h2>Sales Requests</h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
          {SCOPE_LABELS[scopeKind] || SCOPE_LABELS.own}. Click a bucket to submit a new one or review status.
        </p>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          {BUCKET_GROUPS.map(group => {
            const activeForGroup = group.activeKeys
              .map(k => typesByKey[k])
              .filter(Boolean);

            // Hide an entire group only if it has zero of everything.
            const totalCards =
              activeForGroup.length +
              (group.linkedCards?.length || 0) +
              (group.placeholders?.length || 0);
            if (totalCards === 0) return null;

            return (
              <RequestGroup
                key={group.key}
                label={group.label}
                activeBuckets={activeForGroup}
                linkedCards={group.linkedCards || []}
                placeholders={group.placeholders || []}
                order={group.order}
                counts={counts}
                onActiveClick={t => navigate(bucketNavTarget(t.key))}
                onLinkedClick={card => navigate(card.to)}
              />
            );
          })}

          {/* Catch-all for any active bucket key not assigned to a group.
              Protects against silently dropping a new bucket type. */}
          {unmatchedTypes.length > 0 && (
            <RequestGroup
              label="Other"
              activeBuckets={unmatchedTypes}
              linkedCards={[]}
              placeholders={[]}
              counts={counts}
              onActiveClick={t => navigate(bucketNavTarget(t.key))}
              onLinkedClick={() => {}}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ── Group section ────────────────────────────────────────────────
function RequestGroup({ label, activeBuckets, linkedCards, placeholders, order, counts, onActiveClick, onLinkedClick }) {
  // Combine all card kinds into one list, then apply optional explicit
  // ordering by key (Phase 2.3). Default ordering when no `order` array
  // is provided: active → linked → placeholder.
  const combined = [
    ...activeBuckets.map(t   => ({ kind: 'active',      key: t.key,    data: t })),
    ...linkedCards.map(c     => ({ kind: 'linked',      key: c.key,    data: c })),
    ...placeholders.map(p    => ({ kind: 'placeholder', key: p.key,    data: p })),
  ];
  if (Array.isArray(order) && order.length) {
    const rank = new Map(order.map((k, i) => [k, i]));
    combined.sort((a, b) => {
      const ra = rank.has(a.key) ? rank.get(a.key) : Number.POSITIVE_INFINITY;
      const rb = rank.has(b.key) ? rank.get(b.key) : Number.POSITIVE_INFINITY;
      return ra - rb;
    });
  }

  return (
    <section>
      <h3 style={{
        margin: '0 0 16px',
        fontSize: 20,
        fontWeight: 900,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        color: '#0f172a',
        borderLeft: '5px solid #D4A843',
        borderBottom: '2px solid #0f172a',
        paddingLeft: 12,
        paddingBottom: 10,
        paddingTop: 4,
      }}>
        {label}
      </h3>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 16,
      }}>
        {combined.map((item, idx) => {
          // Position-based theme override — 1st card blue, 2nd amber, etc.
          // Applied uniformly across active / linked / coming-soon variants
          // so the visual sequence is identical in every section.
          const themeColor = colorForPosition(idx);
          if (item.kind === 'active') {
            return (
              <ActiveBucketCard
                key={`active-${item.key}`}
                type={item.data}
                count={counts[item.key] ?? 0}
                themeColor={themeColor}
                onClick={() => onActiveClick(item.data)}
              />
            );
          }
          if (item.kind === 'linked') {
            return (
              <LinkedCard
                key={`link-${item.key}`}
                card={item.data}
                themeColor={themeColor}
                onClick={() => onLinkedClick(item.data)}
              />
            );
          }
          return <ComingSoonCard key={`soon-${item.key}`} placeholder={item.data} themeColor={themeColor} />;
        })}
      </div>
    </section>
  );
}

// ── Card variants ────────────────────────────────────────────────
function ActiveBucketCard({ type, count, themeColor, onClick }) {
  // Theme color comes from the section position, not from the bucket itself.
  const color = themeColor || type.color;
  return (
    <button
      onClick={onClick}
      style={{
        textAlign: 'left',
        padding: 20,
        borderRadius: 12,
        border: `1px solid ${color}30`,
        background: `linear-gradient(135deg, ${color}08 0%, transparent 100%)`,
        cursor: 'pointer',
        transition: 'transform 0.1s ease, box-shadow 0.1s ease',
        minHeight: 140,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 4px 16px ${color}25`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 28, lineHeight: 1 }}>{type.icon || '📋'}</span>
        <span style={{ fontWeight: 700, fontSize: 15, color }}>
          {type.label}
        </span>
      </div>
      <div style={{ fontSize: 48, fontWeight: 800, lineHeight: 1, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
        {count}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
        THIS MONTH
      </div>
      {type.description && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.4 }}>
          {type.description}
        </div>
      )}
    </button>
  );
}

function LinkedCard({ card, themeColor, onClick }) {
  const color = themeColor || card.color;
  return (
    <button
      onClick={onClick}
      style={{
        textAlign: 'left',
        padding: 20,
        borderRadius: 12,
        border: `1px solid ${color}30`,
        background: `linear-gradient(135deg, ${color}08 0%, transparent 100%)`,
        cursor: 'pointer',
        transition: 'transform 0.1s ease, box-shadow 0.1s ease',
        minHeight: 140,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 4px 16px ${color}25`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 28, lineHeight: 1 }}>{card.icon || '📋'}</span>
        <span style={{ fontWeight: 700, fontSize: 15, color }}>
          {card.label}
        </span>
      </div>
      <div style={{ marginTop: 'auto', fontSize: 13, fontWeight: 600, color }}>
        Open →
      </div>
      {card.description && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>
          {card.description}
        </div>
      )}
    </button>
  );
}

function ComingSoonCard({ placeholder, themeColor }) {
  // Coming-soon tiles use the position theme but at lower opacity, so the
  // disabled state still reads but the section's color rhythm is preserved.
  const color = themeColor || '#64748b';
  return (
    <div
      aria-disabled="true"
      title="Coming soon"
      style={{
        position: 'relative',
        textAlign: 'left',
        padding: 20,
        borderRadius: 12,
        border: `1px dashed ${color}50`,
        background: `linear-gradient(135deg, ${color}06 0%, transparent 100%)`,
        cursor: 'not-allowed',
        minHeight: 140,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        opacity: 0.7,
        userSelect: 'none',
      }}
    >
      <span style={{
        position: 'absolute', top: 10, right: 10,
        fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
        padding: '3px 8px', borderRadius: 999,
        background: '#0f172a', color: '#fff',
        textTransform: 'uppercase',
      }}>
        Coming Soon
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 28, lineHeight: 1, filter: 'grayscale(0.4)' }}>{placeholder.icon}</span>
        <span style={{ fontWeight: 700, fontSize: 15, color }}>
          {placeholder.label}
        </span>
      </div>
      <div style={{ fontSize: 48, fontWeight: 800, lineHeight: 1, color: '#cbd5e1', fontVariantNumeric: 'tabular-nums' }}>
        —
      </div>
      <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
        Not yet available
      </div>
    </div>
  );
}
